﻿import {
  Markup as ViewerMarkup,
  MarkupPlugin,
  TextMarkup as Web3DTextMarkup,
  MarkupEventName,
} from '@technology/web3d-plugin-markup';
import { Web3DViewer } from '@technology/web3d';
import { detectArrayChanges, Web3DViewerWrapper } from '@trimble/utilities';
import { createEffect, createRoot, on, batch } from 'solid-js';

import { SharedCapabilityProviderBase } from '../base/shared-provider-base';
import { Markup } from './markup';
import {
  processConnectMarkup,
  transformConnectMarkupToMarkup,
  convertViewerMarkupToMarkup,
  addMarkupToViewer,
} from './markup-helpers';
import { hasMarkupGeometryChanged } from './markup-geometry';
import { Markup as ConnectMarkup } from '@data-access/connect/tc-tcps';
import { ANNOTATION_MARKUP_NAMES } from './markup-constants';
import {
  isMarkupGeometryComplete,
  isViewerInImageMode,
  createMarkupOnViewers,
  pruneMarkupsViewerRefs,
  projectMarkupsOntoViewer,
} from './markups-provider-utils';
import { stampCanonicalId } from '../base/viewer-annotation-identity';

/**
 * Sentinel origin used to tag markups created via cross-pane replication.
 * When `markupadded`/`markupmodified` fires with this origin, we skip it to avoid loops.
 */
const REPLICATION_ORIGIN = Symbol('MarkupsProvider.replication');

/**
 * Handles loading, syncing, and managing custom markup types like cloud and redlines.
 * Mirrors the measurements provider architecture: canonical items() store,
 * per-viewer viewerMarkups[], event-driven cross-pane sync.
 */
export class MarkupsCapabilityProvider extends SharedCapabilityProviderBase<Markup> {
  public idKey = 'id' as const;

  private readonly viewerEventListeners = new Map<
    Web3DViewer,
    Array<{ event: string; handler: EventListener }>
  >();

  /**
   * Tracks viewer markups whose multi-tap gesture has started but geometry is
   * not yet complete. Prevents premature replication for line/arrow/text/freeline.
   */
  private readonly pendingCreation = new Set<ViewerMarkup>();

  /**
   * Callback to disable markup tools on other viewers during a multi-tap gesture.
   * Injected by MarkupCapability via `setMultiTapCoordinator`.
   */
  private disableToolOnOtherViewers?: (sourceViewer: Web3DViewer) => void;

  /**
   * Callback to re-enable markup tools on other viewers after replication completes.
   * Injected by MarkupCapability via `setMultiTapCoordinator`.
   */
  private enableToolOnAllViewers?: (excludeViewer: Web3DViewer) => void;

  /**
   * Re-applies the active markup tool on the pane where a markup was just placed.
   * Cloud (single-tap) tools need this after each placement so the tap subscription
   * stays valid (same as toggling the tool off/on).
   */
  private ensureActiveMarkupToolOnViewer?: (viewer: Web3DViewer) => void;

  private effectDispose?: () => void;

  public setMultiTapCoordinator(callbacks: {
    disableToolOnOtherViewers(sourceViewer: Web3DViewer): void;
    enableToolOnAllViewers(excludeViewer: Web3DViewer): void;
    ensureActiveMarkupToolOnViewer?(viewer: Web3DViewer): void;
  }): void {
    this.disableToolOnOtherViewers = callbacks.disableToolOnOtherViewers;
    this.enableToolOnAllViewers = callbacks.enableToolOnAllViewers;
    this.ensureActiveMarkupToolOnViewer =
      callbacks.ensureActiveMarkupToolOnViewer;
  }

  public override removeViewer(viewer: Web3DViewerWrapper): void {
    batch(() => {
      pruneMarkupsViewerRefs(viewer, this.setItems);
      this.removeEventListenersForViewer(viewer);
      super.removeViewer(viewer);
    });
  }

  constructor() {
    super();

    this.effectDispose = createRoot((dispose) => {
      // Sync viewer wrappers: add/remove event listeners.
      createEffect(
        on(this.viewerWrappers, (viewerWrappers, previousViewerWrappers) => {
          const { newItems, removedItems } = detectArrayChanges(
            previousViewerWrappers,
            viewerWrappers
          );

          for (const removed of removedItems) {
            this.removeEventListenersForViewer(removed);
          }

          batch(() => {
            for (const added of newItems) {
              this.addEventListenersToViewer(added);
              projectMarkupsOntoViewer(
                () => this.items(),
                added,
                REPLICATION_ORIGIN,
                this.setItems
              );
            }
          });
        })
      );

      // Sync markup deletion: when items change, remove viewer markups for deleted items.
      createEffect(
        on(this.items, (current, prev) => {
          if (prev == undefined) {
            return;
          }
          const deletedMarkups = prev.filter(
            (prevItem) =>
              !current.some((currItem) =>
                currItem.viewerMarkups.some((vm) =>
                  prevItem.viewerMarkups.some((pvm) => pvm.id === vm.id)
                )
              )
          );

          for (const deleted of deletedMarkups) {
            const ids = deleted.viewerMarkups.map((vm) => vm.id);
            for (const viewerWrapper of this.viewerWrappers()) {
              const markupPlugin =
                viewerWrapper.viewer.plugins.get(MarkupPlugin);
              if (!markupPlugin) continue;

              const markupIds = markupPlugin
                .getMarkups()
                .filter((m) => ids.some((id) => id === m.id))
                .map((m) => m.id);

              if (markupIds.length) {
                const currentIds = new Set(
                  markupPlugin.getMarkups().map((m) => m.id)
                );
                const idsToRemove = markupIds.filter((id) =>
                  currentIds.has(id)
                );
                if (idsToRemove.length > 0) {
                  markupPlugin.removeById(idsToRemove);
                }
              }
            }
          }
        })
      );

      return dispose;
    });
  }

  public cleanup(): Promise<void> {
    this.effectDispose?.();
    this.effectDispose = undefined;

    this.removeAllMarkups();
    this.removeAllEventListeners();
    this.pendingCreation.clear();
    return Promise.resolve(undefined);
  }

  /**
   * Load markups from Connect view data.
   * Supports: Cloud markups, Line markups, Arrow markups, Text markups, Redlines markups
   */
  public loadMarkupsForConnectView(connectMarkups: ConnectMarkup[]): void {
    this.removeAllMarkups();

    const processedMarkups: Markup[] = [];
    for (const connectMarkup of connectMarkups) {
      const processed = processConnectMarkup(connectMarkup);
      if (processed) {
        const transformed = transformConnectMarkupToMarkup(processed);
        if (transformed) {
          processedMarkups.push(transformed);
        }
      }
    }

    batch(() => {
      this.setItems(processedMarkups);

      for (const viewerWrapper of this.viewerWrappers()) {
        projectMarkupsOntoViewer(
          () => this.items(),
          viewerWrapper,
          REPLICATION_ORIGIN,
          this.setItems
        );
      }
    });
  }

  /**
   * Removes any markups whose multi-tap gesture is in progress (geometry not
   * yet complete) from all viewers and clears the pending set. Called when the
   * app needs to abort in-flight markup creation — e.g. when entering Station
   * Navigation while drawing a line/arrow/text markup.
   */
  public discardPendingMarkups(): void {
    if (this.pendingCreation.size === 0) {
      return;
    }

    const pendingIds = new Set([...this.pendingCreation].map((vm) => vm.id));
    this.pendingCreation.clear();

    for (const viewerWrapper of this.viewerWrappers()) {
      const markupPlugin = viewerWrapper.viewer.plugins.get(MarkupPlugin);
      if (!markupPlugin) {
        continue;
      }
      const idsToRemove = markupPlugin
        .getMarkups()
        .filter((m) => pendingIds.has(m.id))
        .map((m) => m.id);
      if (idsToRemove.length > 0) {
        markupPlugin.removeById(idsToRemove);
      }
    }
  }

  /**
   * Remove all markups from viewers and clear internal state.
   * Also removes markups created directly by markup tools that are not
   * tracked in the provider's items.
   */
  public removeAllMarkups(): void {
    this.setItems([]);

    for (const viewerWrapper of this.viewerWrappers()) {
      const markupPlugin = viewerWrapper.viewer.plugins.get(MarkupPlugin);
      if (!markupPlugin) {
        continue;
      }

      const annotationIds = markupPlugin
        .getMarkups()
        .filter((m) => ANNOTATION_MARKUP_NAMES.has(m.name))
        .map((m) => m.id);

      if (annotationIds.length > 0) {
        // Filter out IDs that are not in the plugin to avoid crashes
        const currentIds = new Set(markupPlugin.getMarkups().map((m) => m.id));
        const idsToRemove = annotationIds.filter((id) => currentIds.has(id));
        if (idsToRemove.length > 0) {
          markupPlugin.removeById(idsToRemove);
        }
      }
    }
  }

  // ── Event listener management ──────────────────────────────────────

  private addEventListenersToViewer(viewerWrapper: Web3DViewerWrapper): void {
    const markupAddedHandler = this.handleMarkupAdded(viewerWrapper);
    const markupModifiedHandler = this.handleMarkupModified(viewerWrapper);
    const markupRemovedHandler = this.handleMarkupRemoved;

    viewerWrapper.viewer.addEventListener(
      'markupadded' as MarkupEventName.Added,
      markupAddedHandler
    );
    viewerWrapper.viewer.addEventListener(
      'markupmodified' as MarkupEventName.Modified,
      markupModifiedHandler
    );
    viewerWrapper.viewer.addEventListener(
      'markupremoved' as MarkupEventName.Removed,
      markupRemovedHandler
    );
    this.viewerEventListeners.set(viewerWrapper.viewer, [
      { event: 'markupadded', handler: markupAddedHandler },
      { event: 'markupmodified', handler: markupModifiedHandler },
      { event: 'markupremoved', handler: markupRemovedHandler },
    ]);
  }

  private removeEventListenersForViewer(
    viewerWrapper: Web3DViewerWrapper
  ): void {
    const listeners = this.viewerEventListeners.get(viewerWrapper.viewer);
    if (listeners) {
      for (const { event, handler } of listeners) {
        viewerWrapper.viewer.removeEventListener(event, handler);
      }
      this.viewerEventListeners.delete(viewerWrapper.viewer);
    }
  }

  private removeAllEventListeners(): void {
    this.viewerEventListeners.forEach((listeners, viewer) => {
      for (const { event, handler } of listeners) {
        viewer.removeEventListener(event, handler);
      }
    });
    this.viewerEventListeners.clear();
  }

  // ── Markup event handlers ──────────────────────────────────────────

  private static readonly SINGLE_TAP_TOOLS = new Set(['cloudMarkup']);

  /**
   * Fires when a markup is first created (during constructor).
   * - Cloud (single-tap): add to pendingCreation so we ignore markupmodified from the
   *   constructor color setter (before insert/init). Finalize only once init() has run.
   * - Multi-tap (line, arrow, text, freeline): add to pendingCreation and disable
   *   tools on other viewers. Replication happens in markupmodified when geometry completes.
   * - Measurement and other markup names are ignored (do not touch pendingCreation).
   */
  private readonly handleMarkupAdded =
    (viewerWrapper: Web3DViewerWrapper) =>
    (e: Event): void => {
      const markup = (e as CustomEvent<ViewerMarkup>).detail;
      if (!markup || markup.origin === REPLICATION_ORIGIN) {
        return;
      }

      if (!ANNOTATION_MARKUP_NAMES.has(markup.name)) {
        return;
      }
      this.pendingCreation.add(markup);

      if (!MarkupsCapabilityProvider.SINGLE_TAP_TOOLS.has(markup.name)) {
        // Multi-tap: disable tools on other viewers during gesture.
        this.disableToolOnOtherViewers?.(viewerWrapper.viewer);
      }
    };

  /**
   * Fires when markup geometry is finalised (called by `end()` / `dispatchModifiedEvent()`).
   * For new markups: converts to canonical model, adds to items(), replicates to other viewers.
   * For existing markups: syncs the edit to other viewers.
   */
  private readonly handleMarkupModified =
    (viewerWrapper: Web3DViewerWrapper) =>
    (e: Event): void => {
      queueMicrotask(() => {
        const markup = (e as CustomEvent<ViewerMarkup>).detail;
        if (!markup) {
          return;
        }

        if (!ANNOTATION_MARKUP_NAMES.has(markup.name)) {
          return;
        }

        // Check if this is an edit to an existing tracked markup.
        if (this.updateMarkupForViewerMarkup(markup, viewerWrapper)) {
          return;
        }

        if (markup.origin === REPLICATION_ORIGIN) {
          return;
        }

        // Not a pending creation — nothing to finalize. This happens when
        // duplicate markupmodified events fire (e.g. cloud constructor
        // color setter + init()): the first microtask finalizes and removes
        // from pendingCreation, the second one lands here and is skipped.
        if (!this.pendingCreation.has(markup)) {
          return;
        }

        // Guard stale replica events (NUMARK-797 pattern): rapid freeline sync
        // can replace planar/panorama replicas before their queued microtasks
        // run; without this check those stale events would be finalized again.
        const markupPlugin = viewerWrapper.markupPlugin;
        const isMarkupStillAlive = markupPlugin
          .getMarkups()
          .some((pluginMarkup) => pluginMarkup.id === markup.id);
        if (!isMarkupStillAlive) {
          this.pendingCreation.delete(markup);
          return;
        }

        // For multi-tap tools: wait until geometry is complete.
        if (!isMarkupGeometryComplete(markup)) {
          return;
        }

        // TextMarkup fires markupmodified from both end() and finish().
        // Defer to a microtask so disableEditing() has run: if contentEditable
        // is still "true" this is the end() event, not finish(). Skip.
        if (markup instanceof Web3DTextMarkup) {
          queueMicrotask(() => {
            if (markup.textElement.element.contentEditable !== 'false') {
              return;
            }
            if (!this.pendingCreation.has(markup)) {
              return;
            }
            this.pendingCreation.delete(markup);
            this.finalizeNewMarkup(markup, viewerWrapper);
          });
          return;
        }

        this.pendingCreation.delete(markup);
        this.finalizeNewMarkup(markup, viewerWrapper);
      });
    };

  private readonly handleMarkupRemoved = (e: Event): void => {
    queueMicrotask(() => {
      const markup = (e as CustomEvent<ViewerMarkup>).detail;
      const item = this.items().find((m) =>
        m.viewerMarkups.some((vm) => vm.id === markup.id)
      );
      if (item) {
        this.setItems((prev) => prev.filter((m) => m.id !== item.id));
      }
    });
  };

  // ── Internal helpers ───────────────────────────────────────────────

  /**
   * Checks if a markupmodified event is for an existing tracked markup
   * and, if so, syncs the edit to other viewers.
   * @returns true if an existing item was found and updated.
   */
  private updateMarkupForViewerMarkup(
    viewerMarkup: ViewerMarkup,
    sourceWrapper: Web3DViewerWrapper
  ): boolean {
    const existingItem = this.items().find((m) =>
      m.viewerMarkups.some((vm) => vm.id === viewerMarkup.id)
    );
    if (!existingItem) {
      return false;
    }

    this.syncMarkupWithViewerMarkup(existingItem, viewerMarkup, sourceWrapper);
    return true;
  }

  /**
   * Syncs an edit of a viewer markup to all other viewers.
   * Mirrors measurements' syncMeasurementWithMarkup pattern:
   * rebuild canonical model, remove stale replicas, recreate.
   */
  private syncMarkupWithViewerMarkup(
    item: Markup,
    editedViewerMarkup: ViewerMarkup,
    sourceWrapper: Web3DViewerWrapper
  ): void {
    if (isViewerInImageMode(sourceWrapper.viewer)) {
      return;
    }

    const updatedCanonical = convertViewerMarkupToMarkup(editedViewerMarkup);
    if (!updatedCanonical) {
      return;
    }

    if (!hasMarkupGeometryChanged(item, updatedCanonical)) {
      return;
    }

    // Preserve the existing item id and update the viewerMarkups array.
    const sourceViewerMarkup = item.viewerMarkups.find(
      (vm) => vm.id === editedViewerMarkup.id
    );

    // Remove old replicas from other viewers and recreate.
    const newViewerMarkups: ViewerMarkup[] = sourceViewerMarkup
      ? [sourceViewerMarkup]
      : [];

    for (const viewerWrapper of this.viewerWrappers()) {
      if (viewerWrapper.viewer === sourceWrapper.viewer) {
        continue;
      }

      // Remove existing viewer markups for this item on this viewer.
      const existingIds = item.viewerMarkups
        .filter((vm) => {
          const plugin = viewerWrapper.viewer.plugins.get(MarkupPlugin);
          return plugin
            ?.getMarkups()
            .some((pluginMarkup) => pluginMarkup.id === vm.id);
        })
        .map((vm) => vm.id);

      if (existingIds.length > 0) {
        viewerWrapper.markupPlugin.removeById(existingIds);
      }

      // Create updated replica.
      const replica = addMarkupToViewer(
        { ...updatedCanonical, id: item.id },
        viewerWrapper.viewer,
        REPLICATION_ORIGIN
      );
      if (replica) {
        newViewerMarkups.push(replica);
      }
    }

    // Update the items store with the updated canonical data and new viewerMarkups.
    this.setItems((prev) =>
      prev.map((m) => {
        if (m.id !== item.id) return m;
        return {
          ...updatedCanonical,
          id: item.id,
          viewerMarkups: newViewerMarkups,
        };
      })
    );
  }

  /**
   * Finalizes a newly created markup: converts to canonical model,
   * adds to items(), replicates to other viewers, re-enables tools.
   */
  private finalizeNewMarkup(
    viewerMarkup: ViewerMarkup,
    sourceWrapper: Web3DViewerWrapper
  ): void {
    const canonical = convertViewerMarkupToMarkup(viewerMarkup);
    if (!canonical) {
      return;
    }

    stampCanonicalId(viewerMarkup, canonical.id);

    // Replicate to all other viewers before inserting into the reactive store
    // so that canonical.viewerMarkups is fully populated. Inserting first and
    // then mutating the array in-place would bypass SolidJS reactivity — any
    // effect comparing viewerMarkup IDs (e.g. deletion sync) could see stale data.
    createMarkupOnViewers(
      canonical,
      this.viewerWrappers().filter((vw) => vw.viewer !== sourceWrapper.viewer),
      REPLICATION_ORIGIN
    );

    this.setItems((prev) => [canonical, ...prev]);

    // Re-enable tools on other viewers.
    this.enableToolOnAllViewers?.(sourceWrapper.viewer);

    // Cloud (and any single-tap tool): restart tap subscription on the source pane
    // so the user can place again without toggling the toolbar.
    this.ensureActiveMarkupToolOnViewer?.(sourceWrapper.viewer);
  }
}
